// External Imports
import { h } from 'preact';
import {
  BodyText,
  Card,
  Heading,
  IconGuarantee,
  Inline,
  ShortText,
  Stack,
  Title,
  pxToRem,
  styled,
  useTheme,
} from '@lessonnine/design-system.lib';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

// Internal Imports
import { pxToRemParse } from '../../utils/styles';
import { useStore } from '../../state/context';
import { LayoutWithSiteMaxWidth } from '../LayoutWithSiteMaxWidth/LayoutWithSiteMaxWidth';

// Local Variables
const desktopBreakpoint = parseInt(tokens.viewports.breakpoints.medium, 10);
const LanguageCardLink = styled.a`
  text-decoration: none;
`;

const SelectLanguageContainer = styled(Inline)`
  max-width: ${pxToRem(375)};
  @media (min-width: ${pxToRem(desktopBreakpoint)}) {
    max-width: 100%;
  }
`;

const SelectLanguageWrapper = styled(Stack)`
  background-color: ${({ theme }) => theme.color.brand.default};
`;

const LanguageCard = styled(Card)`
  min-width: ${pxToRem(180)};
`;

function SelectLanguageSection() {
  const { getDropdownLink, learnLanguages, t } = useStore();
  const theme = useTheme();
  return (
    <SelectLanguageWrapper
      alignItems="center"
      padding={{ medium: [pxToRem(80), pxToRem(302)], small: [pxToRem(48), pxToRem(24)] }}
    >
      <LayoutWithSiteMaxWidth
        align="center"
        alignItems="center"
        direction="stack"
        gap={tokens.space.x8}
        padding={[pxToRemParse(tokens.space.x12), pxToRemParse(tokens.space.x4)]}
      >
        <Title
          as="h2"
          color="inverse"
          size={{ medium: 'default', small: 'xsmall' }}
          textAlign="center"
        >
          {t.cta_section.title}
        </Title>
        <Stack gap={20}>
          <Heading
            color="inverse"
            fontSize={{ medium: 'default', small: 'small' }}
            textAlign="center"
          >
            {t.cta_section.subtitle}
          </Heading>
          <SelectLanguageContainer align="center" forwardedAs="ul" gap={15} padding={0} wrap>
            {learnLanguages.map(({ code, Icon, name }) => (
              <Inline as="li" key={name}>
                <LanguageCardLink href={getDropdownLink(code)}>
                  <LanguageCard>
                    <Inline align="center" alignItems="center" gap={10}>
                      <Icon />
                      <ShortText size="small" weight="strong">
                        {name}
                      </ShortText>
                    </Inline>
                  </LanguageCard>
                </LanguageCardLink>
              </Inline>
            ))}
          </SelectLanguageContainer>
        </Stack>
        <Stack alignItems="center" gap={20}>
          <IconGuarantee color={theme.color.neutral.surfaceLightened} size="xlarge" />
          <BodyText
            color="inverse"
            size={{ medium: 'large', small: 'default' }}
            textAlign="center"
            weight="strong"
          >
            {t.cta_section.guarantee}
          </BodyText>
        </Stack>
      </LayoutWithSiteMaxWidth>
    </SelectLanguageWrapper>
  );
}

export { SelectLanguageSection };
